import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Check,
  CheckCircle2,
  Circle,
  Copy,
  Plus,
  Search,
  Send,
  Users,
  X,
} from "lucide-react";
import { useToast } from "@/shared/ui/Toast";
import { ConfirmModal } from "@/shared/ui/ConfirmModal";
import { Input } from "@/shared/ui/Input";
import { Textarea } from "@/shared/ui/Textarea";
import { Checkbox } from "@/shared/ui/Checkbox";
import {
  Select as RadixSelect,
  SelectContent as RadixSelectContent,
  SelectItem as RadixSelectItem,
  SelectTrigger as RadixSelectTrigger,
  SelectValue as RadixSelectValue,
} from "@/shared/ui/Select";
import {
  useCancelScheduled,
  useMerchantSearch,
  useSaveDraft,
  useSendBroadcast,
  useSentNotifications,
} from "./hooks";
import { estimateAudience } from "./mock";
import { formatCount, formatNotifDate } from "./notifDate";
import { NotificationError, supportsEstimate, type NotificationErrorCode } from "./api";
import type {
  Channel,
  MerchantOption,
  RecipientMode,
  SendMode,
  SentNotification,
} from "./types";

// Map a normalised error code to its inline message key.
const ERROR_KEY: Record<NotificationErrorCode, string> = {
  contentRequired: "notifications.contentRequired",
  forbidden: "notifications.forbidden",
  generic: "notifications.sendError",
};

// Content languages — both are mandatory to send (task 0081).
const LANGS = ["uz", "ru"] as const;
type Lang = (typeof LANGS)[number];
const LANG_KEY: Record<Lang, string> = {
  uz: "notifications.langUz",
  ru: "notifications.langRu",
};
// Inline "the other language is still empty" warning, keyed by which language is missing.
const INCOMPLETE_WARN_KEY: Record<Lang, string> = {
  uz: "notifications.incompleteWarnUz",
  ru: "notifications.incompleteWarnRu",
};

const RECIPIENT_MODES: RecipientMode[] = ["all", "segment", "single"];
const CHANNELS: Channel[] = ["push", "sms", "both"];
// Segment status options mirror the backend UserStatus (ACTIVE / INACTIVE / NEW).
const STATUS_OPTIONS = [
  "segStatusAll",
  "segStatusActive",
  "segStatusInactive",
  "segStatusNew",
];
const RECIPIENT_KEY: Record<RecipientMode, string> = {
  all: "notifications.recipientAll",
  segment: "notifications.recipientSegment",
  single: "notifications.recipientSingle",
};
const CHANNEL_KEY: Record<Channel, string> = {
  push: "notifications.channelPush",
  sms: "notifications.channelSms",
  both: "notifications.channelBoth",
};
const CHANNEL_TABLE_KEY: Record<Channel, string> = {
  push: "notifications.channelPush",
  sms: "notifications.channelSms",
  both: "notifications.channelBothTable",
};
const BODY_MAX = 178;

// Personalization token (task 0084). Must match the backend NAME_TOKEN exactly — the chip inserts this
// literal and the backend swaps it for each recipient's first_name at send time. The preview renders a
// sample name so the operator sees the shape; split/join replaces every occurrence (mirrors the backend's
// str.replace, unlike String.replace which only hits the first).
const NAME_TOKEN = "{name}";
const SAMPLE_NAME = "Dilshod"; // preview-only placeholder for {name}
const previewName = (value: string) => value.split(NAME_TOKEN).join(SAMPLE_NAME);

const TOTAL_CUSTOMERS = 64280; // mirrors the "Barcha mijozlar" recipient pill count (mock estimate)

// Format a Date as the value a <input type="datetime-local"> expects (local time, no zone):
// "YYYY-MM-DDTHH:mm". Used for the picker's `min` (now) and to bound the schedule to the future.
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function NotificationsScreen() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const send = useSendBroadcast();
  const draft = useSaveDraft();

  const titleId = useId();
  const bodyId = useId();
  const scheduleId = useId();

  // Refs to the active-language title/body fields so the {name} chip can splice the token in at the
  // caret (not just append) and then restore focus + cursor (task 0084).
  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [recipientMode, setRecipientMode] = useState<RecipientMode>("all");
  const [customerStatus, setCustomerStatus] = useState("segStatusAll");
  const [segmentPremium, setSegmentPremium] = useState(false);
  // SINGLE picker: free-text search → debounced lookup → a chosen merchant (target_user).
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantOption | null>(null);
  const [targetUserId, setTargetUserId] = useState<number | null>(null);
  const [channel, setChannel] = useState<Channel>("push");
  // Bilingual content — both languages are kept in state; the active tab swaps which pair is editable.
  const [titleUz, setTitleUz] = useState("");
  const [titleRu, setTitleRu] = useState("");
  const [bodyUz, setBodyUz] = useState("");
  const [bodyRu, setBodyRu] = useState("");
  const [activeLang, setActiveLang] = useState<Lang>("uz");
  // Which language the right-hand preview shows. Follows the edit tab, but the operator can peek the
  // other language without leaving the field they're editing (dual-language preview).
  const [previewLang, setPreviewLang] = useState<Lang>("uz");
  const [sendMode, setSendMode] = useState<SendMode>("now");
  const [scheduleAt, setScheduleAt] = useState(""); // datetime-local string while sendMode=schedule
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<NotificationErrorCode | null>(null);

  // Debounce the search box so the merchant lookup fires on a pause, not every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(id);
  }, [query]);

  // Lookup is disabled once a merchant is picked (the chip replaces the search box).
  const merchantsQ = useMerchantSearch(selectedMerchant ? "" : debouncedQuery);
  // Treat the pre-debounce gap as "still searching" so the dropdown never flashes "not found".
  const merchantsLoading =
    !selectedMerchant &&
    query.trim() !== "" &&
    (query.trim() !== debouncedQuery.trim() || merchantsQ.isFetching);
  const merchants = merchantsQ.data ?? [];

  function selectMerchant(m: MerchantOption) {
    setSelectedMerchant(m);
    setTargetUserId(m.id);
    setQuery(`${m.firstName} ${m.lastName}`.trim());
    setError(null);
  }
  function clearMerchant() {
    setSelectedMerchant(null);
    setTargetUserId(null);
    setQuery("");
  }

  // Switch the edit tab and keep the preview in step with it.
  function switchLang(l: Lang) {
    setActiveLang(l);
    setPreviewLang(l);
    setError(null);
  }

  // Active-tab bindings (the title/body inputs edit whichever language is selected).
  const title = activeLang === "uz" ? titleUz : titleRu;
  const body = activeLang === "uz" ? bodyUz : bodyRu;
  const setTitle = activeLang === "uz" ? setTitleUz : setTitleRu;
  const setBody = activeLang === "uz" ? setBodyUz : setBodyRu;
  const otherLang: Lang = activeLang === "uz" ? "ru" : "uz";

  // Insert the {name} token at the caret of the given field (falls back to appending when the field
  // isn't focused), then restore focus with the cursor just past the token. requestAnimationFrame waits
  // for React to flush the new value before we set the selection range (task 0084).
  function insertNameToken(
    el: HTMLInputElement | HTMLTextAreaElement | null,
    value: string,
    setValue: (v: string) => void,
  ) {
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + NAME_TOKEN + value.slice(end));
    setError(null);
    const caret = start + NAME_TOKEN.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }

  // Copy the active language's title + body into the other language as a starting point for
  // translation — the single biggest time-saver when both languages carry the same message.
  function copyToOther() {
    if (activeLang === "uz") {
      setTitleRu(titleUz);
      setBodyRu(bodyUz);
    } else {
      setTitleUz(titleRu);
      setBodyUz(bodyRu);
    }
    setError(null);
    showToast(t("notifications.copied"));
  }

  // Per-language completeness (both title + body filled) — drives the tab dots + checklist.
  const langComplete: Record<Lang, boolean> = {
    uz: titleUz.trim() !== "" && bodyUz.trim() !== "",
    ru: titleRu.trim() !== "" && bodyRu.trim() !== "",
  };

  // Schedule must point to the future (mirrors the serializer guard). Converted to a UTC
  // ISO instant only when actually scheduling, so the backend never has to guess the zone.
  const scheduleFuture = scheduleAt !== "" && new Date(scheduleAt).getTime() > Date.now();
  const scheduledAtIso =
    sendMode === "schedule" && scheduleFuture ? new Date(scheduleAt).toISOString() : null;

  const input = {
    recipientMode,
    customerStatus,
    segmentPremium,
    query,
    targetUserId,
    channel,
    titleUz,
    titleRu,
    textUz: bodyUz,
    textRu: bodyRu,
    sendMode,
    scheduledAt: scheduledAtIso,
  };
  const audience = estimateAudience(input);
  // Both languages are mandatory (task 0081); a SINGLE broadcast needs a chosen merchant
  // (the backend requires target_user even for a draft). A scheduled send additionally needs
  // a future time — but a draft ignores the schedule, so that only blocks the send button.
  const contentOk = langComplete.uz && langComplete.ru;
  const targetOk = recipientMode !== "single" || targetUserId != null;
  const timingOk = sendMode !== "schedule" || scheduleFuture;
  const canDraft = contentOk && targetOk;
  const canSend = canDraft && timingOk;
  const busy = send.isPending || draft.isPending;

  // Step-completion drives the numbered badges (a green check once a step is satisfied).
  const stepDone = { recipients: targetOk, channel: true, message: contentOk, timing: timingOk };

  // Trim every language field before handing the payload to the API.
  const trimmedInput = () => ({
    ...input,
    titleUz: titleUz.trim(),
    titleRu: titleRu.trim(),
    textUz: bodyUz.trim(),
    textRu: bodyRu.trim(),
  });

  function resetContent() {
    setTitleUz("");
    setTitleRu("");
    setBodyUz("");
    setBodyRu("");
    setActiveLang("uz");
    setPreviewLang("uz");
    setScheduleAt(""); // the schedule time is per-message — clear it with the content
  }

  async function doSend() {
    setError(null);
    try {
      await send.mutateAsync(trimmedInput());
      setConfirmOpen(false);
      showToast(t("notifications.sent"));
      // Reset the message; keep the targeting/channel choices for the next one.
      resetContent();
    } catch (e) {
      setConfirmOpen(false);
      setError(e instanceof NotificationError ? e.code : "generic");
    }
  }

  async function doSaveDraft() {
    if (!canDraft || busy) return;
    setError(null);
    try {
      await draft.mutateAsync(trimmedInput());
      showToast(t("notifications.draftSaved"));
      resetContent();
    } catch (e) {
      setError(e instanceof NotificationError ? e.code : "generic");
    }
  }

  // Channel-aware reach copy for the stats card (mock-only — gated by supportsEstimate).
  const reachText =
    channel === "push"
      ? t("notifications.reachPush", { reach: formatCount(audience.reach) })
      : channel === "sms"
        ? t("notifications.reachSms", { total: formatCount(audience.total) })
        : t("notifications.reachBoth", { total: formatCount(audience.total) });

  return (
    <div className="mx-auto max-w-[1040px]">
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* ── Compose ─────────────────────────────────────────────────────── */}
        <section className="rounded-card border border-grey-200 bg-white p-4 shadow-card sm:p-6">
          <h2 className="text-[16px] font-bold text-grey-900">
            {t("notifications.composeTitle")}
          </h2>

          <form
            className="mt-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSend && !busy) setConfirmOpen(true);
            }}
          >
            {/* Step 1 — Recipients */}
            <Step n={1} title={t("notifications.recipientsLabel")} done={stepDone.recipients} first>
              <div className="flex flex-wrap gap-2">
                {RECIPIENT_MODES.map((m) => (
                  <Pill
                    key={m}
                    selected={recipientMode === m}
                    onClick={() => {
                      setRecipientMode(m);
                      // Leaving SINGLE drops the chosen merchant so a stray target_user never lingers.
                      if (m !== "single") clearMerchant();
                      setError(null);
                    }}
                  >
                    {m === "all" && supportsEstimate
                      ? `${t("notifications.recipientAll")} · ${formatCount(TOTAL_CUSTOMERS)}`
                      : t(RECIPIENT_KEY[m])}
                  </Pill>
                ))}
              </div>

              {/* Segment filters */}
              {recipientMode === "segment" && (
                <div className="mt-4 flex flex-col gap-3">
                  <Select
                    label={t("notifications.segStatus")}
                    value={customerStatus}
                    onChange={setCustomerStatus}
                    options={STATUS_OPTIONS.map((o) => ({ value: o, label: t(`notifications.${o}`) }))}
                  />
                  <label className="flex cursor-pointer select-none items-center gap-2.5">
                    <Checkbox
                      checked={segmentPremium}
                      onCheckedChange={(v) => setSegmentPremium(v === true)}
                    />
                    <span className="text-[13.5px] font-medium text-grey-700">
                      {t("notifications.segPremium")}
                    </span>
                  </label>
                </div>
              )}

              {/* Single recipient — search merchants, pick one (its id → target_user). */}
              {recipientMode === "single" && (
                <div className="mt-4">
                  <span className="mb-2 block text-[13px] font-semibold text-grey-700">
                    {t("notifications.singleSelect")}
                  </span>
                  {selectedMerchant ? (
                    <div className="flex items-center justify-between gap-3 rounded-control border border-green bg-green-bg-soft px-3.5 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-semibold text-grey-900">
                          {selectedMerchant.firstName} {selectedMerchant.lastName}
                        </p>
                        <p className="truncate text-[12.5px] text-grey-500">
                          {selectedMerchant.phoneNumber} ·{" "}
                          {t("notifications.merchantClients", {
                            n: formatCount(selectedMerchant.clientCount),
                          })}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={clearMerchant}
                        className="flex flex-none items-center gap-1 text-[13px] font-semibold text-grey-600 transition active:scale-95 hover:text-green"
                      >
                        <X size={14} strokeWidth={2} />
                        {t("notifications.merchantChange")}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search
                          size={17}
                          strokeWidth={1.8}
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-grey-400"
                        />
                        {/* pl-10 preserves space for the leading Search icon */}
                        <Input
                          type="text"
                          value={query}
                          onChange={(e) => {
                            setQuery(e.target.value);
                            setError(null);
                          }}
                          placeholder={t("notifications.singlePlaceholder")}
                          className="pl-10"
                        />
                      </div>
                      {query.trim() !== "" && (
                        <div className="mt-1.5 overflow-hidden rounded-control border border-grey-200 bg-white">
                          {merchantsLoading ? (
                            [0, 1, 2].map((i) => (
                              <div key={i} className="px-3.5 py-2.5">
                                <div className="h-3.5 w-32 animate-pulse rounded bg-grey-100" />
                                <div className="mt-1.5 h-3 w-24 animate-pulse rounded bg-grey-100" />
                              </div>
                            ))
                          ) : merchants.length > 0 ? (
                            <ul className="divide-y divide-grey-100">
                              {merchants.map((m) => (
                                <li key={m.id}>
                                  <button
                                    type="button"
                                    onClick={() => selectMerchant(m)}
                                    className="flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-grey-50"
                                  >
                                    <span className="min-w-0">
                                      <span className="block truncate text-[13.5px] font-semibold text-grey-900">
                                        {m.firstName} {m.lastName}
                                      </span>
                                      <span className="block truncate text-[12px] text-grey-500">
                                        {m.phoneNumber}
                                      </span>
                                    </span>
                                    <span className="flex-none text-[12px] text-grey-400">
                                      {t("notifications.merchantClients", {
                                        n: formatCount(m.clientCount),
                                      })}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="px-3.5 py-3 text-[13px] text-grey-500">
                              {t("notifications.merchantNotFound")}
                            </p>
                          )}
                        </div>
                      )}
                      <p className="mt-2 text-[12px] text-grey-400">
                        {t("notifications.merchantPickHint")}
                      </p>
                    </>
                  )}
                </div>
              )}
            </Step>

            {/* Step 2 — Channel */}
            <Step n={2} title={t("notifications.channelLabel")} done={stepDone.channel}>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map((c) => (
                  <Pill key={c} selected={channel === c} onClick={() => setChannel(c)}>
                    {t(CHANNEL_KEY[c])}
                  </Pill>
                ))}
              </div>
            </Step>

            {/* Step 3 — Message (bilingual; both UZ + RU required to send) */}
            <Step n={3} title={t("notifications.stepMessage")} done={stepDone.message}>
              {/* Language tabs — a green dot marks a complete tab, an amber dot one still to fill. */}
              <div className="inline-flex rounded-pill border border-grey-200 bg-grey-50 p-1">
                {LANGS.map((l) => {
                  const active = activeLang === l;
                  return (
                    <button
                      key={l}
                      type="button"
                      onClick={() => switchLang(l)}
                      className={`flex h-8 items-center gap-1.5 rounded-pill px-4 text-[13px] font-semibold transition ${
                        active
                          ? "bg-white text-grey-900 shadow-card"
                          : "text-grey-500 hover:text-grey-700"
                      }`}
                    >
                      {t(LANG_KEY[l])}
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          langComplete[l] ? "bg-green" : "bg-warning"
                        }`}
                        aria-hidden
                      />
                    </button>
                  );
                })}
              </div>

              {/* Title */}
              <div className="mt-4">
                <label
                  htmlFor={titleId}
                  className="mb-2 block text-[13px] font-semibold text-grey-700"
                >
                  {t("notifications.title")}{" "}
                  <span className="font-normal text-grey-400">{t("notifications.titleHint")}</span>
                </label>
                <Input
                  id={titleId}
                  ref={titleRef}
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setError(null);
                  }}
                  placeholder={t("notifications.titlePlaceholder")}
                  className="text-[15px]"
                />
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <NameChip
                    label={t("notifications.insertNameChip")}
                    onClick={() => insertNameToken(titleRef.current, title, setTitle)}
                  />
                  <span className="text-[12px] text-grey-400">
                    {t("notifications.insertNameHint")}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="mt-5">
                <label
                  htmlFor={bodyId}
                  className="mb-2 block text-[13px] font-semibold text-grey-700"
                >
                  {t("notifications.bodyLabel")}
                </label>
                <Textarea
                  id={bodyId}
                  ref={bodyRef}
                  value={body}
                  maxLength={BODY_MAX}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setError(null);
                  }}
                  rows={4}
                  placeholder={t("notifications.bodyPlaceholder")}
                  className="leading-snug resize-y"
                />
                {/* Helpers: insert {name}, and copy this language to the other as a translation base. */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <NameChip
                    label={t("notifications.insertNameChip")}
                    onClick={() => insertNameToken(bodyRef.current, body, setBody)}
                  />
                  <button
                    type="button"
                    onClick={copyToOther}
                    disabled={title.trim() === "" && body.trim() === ""}
                    className="inline-flex items-center gap-1 rounded-pill border border-grey-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-grey-700 transition active:scale-95 hover:border-green hover:text-green disabled:opacity-40 disabled:hover:border-grey-300 disabled:hover:text-grey-700"
                  >
                    <Copy size={13} strokeWidth={2} />
                    {t(otherLang === "ru" ? "notifications.copyToRu" : "notifications.copyToUz")}
                  </button>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[12px] text-grey-400">
                  <span>{t("notifications.bodyHelp")}</span>
                  <span className="tnum">
                    {body.length} / {BODY_MAX}
                  </span>
                </div>
              </div>

              {/* Smart nudge: the other language is still empty — one click jumps straight to it. */}
              {!langComplete[otherLang] && (
                <button
                  type="button"
                  onClick={() => switchLang(otherLang)}
                  className="mt-3 flex w-full items-center gap-2 rounded-control border border-warning/30 bg-warning-bg px-3.5 py-2.5 text-left text-[13px] font-medium text-warning transition active:scale-[0.99] hover:border-warning/50"
                >
                  <AlertTriangle size={15} strokeWidth={2} className="flex-none" />
                  <span className="flex-1">{t(INCOMPLETE_WARN_KEY[otherLang])}</span>
                  <ArrowRight size={15} strokeWidth={2} className="flex-none" />
                </button>
              )}
            </Step>

            {/* Step 4 — Send time */}
            <Step n={4} title={t("notifications.sendTimeLabel")} done={stepDone.timing}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <RadioCard
                  selected={sendMode === "now"}
                  onClick={() => setSendMode("now")}
                  title={t("notifications.sendNow")}
                  hint={t("notifications.sendNowHint")}
                />
                <RadioCard
                  selected={sendMode === "schedule"}
                  onClick={() => setSendMode("schedule")}
                  title={t("notifications.sendSchedule")}
                  hint={t("notifications.sendScheduleHint")}
                />
              </div>
              {sendMode === "schedule" && (
                <div className="mt-3">
                  <label
                    htmlFor={scheduleId}
                    className="mb-2 block text-[13px] font-semibold text-grey-700"
                  >
                    {t("notifications.scheduleAtLabel")}
                  </label>
                  {/* type="datetime-local" + min/onChange are preserved exactly — the local-time format
                      (toLocalInput) and value string are unchanged; Input forwards all native props. */}
                  <Input
                    id={scheduleId}
                    type="datetime-local"
                    value={scheduleAt}
                    min={toLocalInput(new Date())}
                    onChange={(e) => {
                      setScheduleAt(e.target.value);
                      setError(null);
                    }}
                    className="sm:w-auto"
                  />
                  {scheduleAt !== "" && !scheduleFuture && (
                    <p className="mt-2 text-[12px] font-medium text-red">
                      {t("notifications.scheduleFutureError")}
                    </p>
                  )}
                </div>
              )}
            </Step>

            {/* Readiness checklist — always tells the operator exactly what is left before sending. */}
            <div className="mt-6 rounded-control border border-grey-200 bg-grey-50 p-4">
              <p className="mb-2.5 text-[12px] font-semibold uppercase tracking-[0.06em] text-grey-500">
                {t("notifications.readyTitle")}
              </p>
              <div className="flex flex-col gap-1.5">
                <CheckItem ok={targetOk} label={t("notifications.readyRecipient")} />
                <CheckItem ok={langComplete.uz} label={t("notifications.readyUz")} />
                <CheckItem ok={langComplete.ru} label={t("notifications.readyRu")} />
                {sendMode === "schedule" && (
                  <CheckItem ok={scheduleFuture} label={t("notifications.readyTiming")} />
                )}
              </div>
            </div>

            {error && (
              <p role="alert" className="mt-4 text-[13px] font-medium text-red">
                {t(ERROR_KEY[error])}
              </p>
            )}

            {/* Footer */}
            <div className="mt-5 flex justify-end gap-3 border-t border-grey-100 pt-5">
              <button
                type="button"
                onClick={doSaveDraft}
                disabled={!canDraft || busy}
                className="h-11 rounded-control border border-grey-300 bg-white px-4 text-[14px] font-semibold text-grey-700 transition active:scale-[0.98] hover:bg-grey-50 disabled:opacity-50 disabled:active:scale-100"
              >
                {draft.isPending ? t("notifications.sending") : t("notifications.saveDraft")}
              </button>
              <button
                type="submit"
                disabled={!canSend || busy}
                className="flex h-11 items-center gap-2 rounded-control bg-green px-5 text-[14px] font-semibold text-white transition active:scale-[0.98] hover:bg-green-dark disabled:opacity-50 disabled:active:scale-100"
              >
                <Send size={17} strokeWidth={2} />
                {send.isPending ? t("notifications.sending") : t("notifications.send")}
              </button>
            </div>
          </form>
        </section>

        {/* ── Preview + stats ─────────────────────────────────────────────── */}
        {/* Right column sticks while the compose form scrolls, so the live preview stays in view. */}
        <aside className="flex flex-col gap-4 self-start lg:sticky lg:top-6">
          <PreviewCard
            lang={previewLang}
            onLangChange={setPreviewLang}
            titleUz={titleUz}
            titleRu={titleRu}
            bodyUz={bodyUz}
            bodyRu={bodyRu}
          />
          <section className="rounded-card border border-grey-200 bg-white p-4 shadow-card sm:p-5">
            {supportsEstimate ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-pill bg-green-bg text-green-dark">
                    <Users size={20} strokeWidth={1.8} />
                  </span>
                  <div className="leading-tight">
                    <div className="tnum text-[26px] font-bold text-grey-900">
                      {formatCount(audience.total)}
                    </div>
                    <div className="text-[13px] text-grey-500">{t("notifications.statsUnit")}</div>
                  </div>
                </div>
                <p className="mt-3 text-[12.5px] leading-relaxed text-grey-500">{reachText}</p>
              </>
            ) : (
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-pill bg-green-bg text-green-dark">
                  <Users size={20} strokeWidth={1.8} />
                </span>
                <p className="text-[12.5px] leading-relaxed text-grey-500">
                  {t("notifications.statsRealNote")}
                </p>
              </div>
            )}
          </section>
        </aside>
      </div>

      {/* ── Sent history ──────────────────────────────────────────────────── */}
      <h2 className="mb-3 mt-8 text-[16px] font-bold text-grey-900">
        {t("notifications.historyTitle")}
      </h2>
      <HistoryTable />

      <ConfirmModal
        open={confirmOpen}
        tone="warning"
        title={t("notifications.confirmTitle")}
        message={
          supportsEstimate
            ? t("notifications.confirmBody", { n: formatCount(audience.total) })
            : t("notifications.confirmBodyNoCount")
        }
        confirmLabel={t("notifications.confirmYes")}
        cancelLabel={t("notifications.cancel")}
        busy={send.isPending}
        onConfirm={doSend}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

// ── Small building blocks ──────────────────────────────────────────────────

// A numbered, divider-separated compose step. The badge flips to a green check once the step
// is satisfied, giving the operator a lightweight sense of progress down the form.
function Step({
  n,
  title,
  done,
  first,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  first?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={first ? "" : "mt-5 border-t border-grey-100 pt-5"}>
      <div className="mb-3 flex items-center gap-2.5">
        <span
          className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-[12px] font-bold transition-colors ${
            done ? "bg-green text-white" : "bg-grey-100 text-grey-500"
          }`}
        >
          {done ? <Check size={14} strokeWidth={3} /> : n}
        </span>
        <h3 className="text-[14px] font-semibold text-grey-800">{title}</h3>
      </div>
      {children}
    </section>
  );
}

// One row of the readiness checklist: green tick when satisfied, hollow grey circle when not.
function CheckItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-[13px]">
      {ok ? (
        <CheckCircle2 size={16} strokeWidth={2} className="flex-none text-green" />
      ) : (
        <Circle size={16} strokeWidth={2} className="flex-none text-grey-300" />
      )}
      <span className={ok ? "text-grey-700" : "text-grey-400"}>{label}</span>
    </div>
  );
}

// Pill button that splices the {name} personalization token into a field at the caret (task 0084).
function NameChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-pill border border-grey-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-grey-700 transition active:scale-95 hover:border-green hover:text-green"
    >
      <Plus size={13} strokeWidth={2.2} />
      {label}
    </button>
  );
}

function Pill({
  selected,
  onClick,
  disabled,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`h-9 rounded-pill border px-3.5 text-[13px] font-semibold transition active:scale-95 ${
        disabled
          ? "cursor-not-allowed border-grey-200 bg-grey-50 text-grey-300 active:scale-100"
          : selected
            ? "border-green bg-green text-white"
            : "border-grey-300 bg-white text-grey-700 hover:bg-grey-50"
      }`}
    >
      {children}
    </button>
  );
}

// Segment status selector — brand-themed Radix Select replaces the native <select>.
// All option values are non-empty strings (STATUS_OPTIONS), so SELECT_NONE is not needed.
function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <span className="mb-2 block text-[13px] font-semibold text-grey-700">{label}</span>
      <RadixSelect value={value} onValueChange={onChange}>
        <RadixSelectTrigger>
          <RadixSelectValue />
        </RadixSelectTrigger>
        <RadixSelectContent>
          {options.map((o) => (
            <RadixSelectItem key={o.value} value={o.value}>
              {o.label}
            </RadixSelectItem>
          ))}
        </RadixSelectContent>
      </RadixSelect>
    </div>
  );
}

function RadioCard({
  selected,
  onClick,
  title,
  hint,
  chip,
  disabled,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  hint: string;
  chip?: string;
  disabled?: boolean;
}) {
  const active = selected && !disabled;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-control border p-3.5 text-left transition active:scale-[0.98] ${
        disabled
          ? "cursor-not-allowed border-grey-200 bg-grey-50 active:scale-100"
          : active
            ? "border-green bg-green-bg-soft"
            : "border-grey-200 bg-white hover:bg-grey-50"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-2 ${
            active ? "border-green" : "border-grey-300"
          }`}
        >
          {active && <span className="h-2 w-2 rounded-full bg-green" />}
        </span>
        <span
          className={`text-[14px] font-semibold ${disabled ? "text-grey-400" : "text-grey-900"}`}
        >
          {title}
        </span>
      </div>
      <p className="mt-1.5 text-[12.5px] text-grey-500">{hint}</p>
      {chip && (
        <span className="mt-2 inline-block rounded-pill bg-white px-2.5 py-1 text-[12px] font-semibold text-grey-700 ring-1 ring-grey-200">
          {chip}
        </span>
      )}
    </button>
  );
}

// Live push-notification preview. Has its own UZ/RU toggle so the operator can check either
// language's wording without leaving the field they're editing (dual-language preview).
function PreviewCard({
  lang,
  onLangChange,
  titleUz,
  titleRu,
  bodyUz,
  bodyRu,
}: {
  lang: Lang;
  onLangChange: (l: Lang) => void;
  titleUz: string;
  titleRu: string;
  bodyUz: string;
  bodyRu: string;
}) {
  const { t } = useTranslation();
  const title = previewName(lang === "uz" ? titleUz : titleRu);
  const body = previewName(lang === "uz" ? bodyUz : bodyRu);
  return (
    <section className="rounded-card border border-grey-200 bg-white p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-grey-400">
          {t("notifications.previewLabel")}
        </p>
        <div className="inline-flex rounded-pill border border-grey-200 bg-grey-50 p-0.5">
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => onLangChange(l)}
              className={`rounded-pill px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
                lang === l ? "bg-white text-grey-800 shadow-card" : "text-grey-400 hover:text-grey-600"
              }`}
            >
              {t(LANG_KEY[l])}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-control bg-grey-50 p-3">
        <div className="flex gap-3 rounded-[12px] border border-grey-200 bg-white p-3.5 shadow-card">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[8px] bg-green">
            <img src="/assets/n-white.svg" alt="Nasiyah" className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-grey-400">
              NASIYAH · {t("notifications.previewNow")}
            </p>
            <p
              className={`mt-0.5 text-[14px] font-bold ${
                title.trim() ? "text-grey-900" : "text-grey-400"
              }`}
            >
              {title.trim() || t("notifications.previewTitlePlaceholder")}
            </p>
            <p
              className={`mt-0.5 line-clamp-3 text-[12.5px] leading-snug ${
                body.trim() ? "text-grey-600" : "text-grey-400"
              }`}
            >
              {body.trim() || t("notifications.previewBodyPlaceholder")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── History table ──────────────────────────────────────────────────────────
function HistoryTable() {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { data, isLoading, isError } = useSentNotifications();
  const cancel = useCancelScheduled();
  const lang = i18n.language.startsWith("ru") ? "ru" : "uz";

  // Only one cancel is in flight at a time; disable just the row whose id is being cancelled.
  const cancelingId = cancel.isPending ? (cancel.variables ?? null) : null;
  function onCancel(id: number) {
    cancel.mutate(id, { onSuccess: () => showToast(t("notifications.cancelled")) });
  }

  const headCell = "px-4 py-2.5 text-[12px] font-semibold text-grey-500";

  if (isError) {
    return (
      <div className="rounded-card border border-grey-200 bg-white px-4 py-6 shadow-card">
        <p className="text-[13.5px] font-medium text-red">{t("notifications.loadError")}</p>
      </div>
    );
  }

  if (data && data.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-card border border-dashed border-grey-200 bg-white px-6 py-12 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-pill bg-grey-100 text-grey-400">
          <Bell size={20} strokeWidth={1.8} />
        </div>
        <p className="mt-3 text-[14px] font-semibold text-grey-700">{t("notifications.empty")}</p>
        <p className="mt-1 text-[13px] text-grey-500">{t("notifications.emptyHint")}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-grey-200 bg-white shadow-card">
      {/* Phones: one card per broadcast instead of a 6-column table scrolled sideways. */}
      <ul className="divide-y divide-grey-100 md:hidden">
        {isLoading || !data
          ? [0, 1, 2].map((i) => <HistoryCardSkeleton key={i} />)
          : data.map((n) => (
              <HistoryCard
                key={n.id}
                item={n}
                lang={lang}
                onCancel={onCancel}
                canceling={cancelingId === n.id}
              />
            ))}
      </ul>

      <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[720px] text-left">
        <thead className="bg-grey-50">
          <tr>
            <th className={headCell}>{t("notifications.colTitle")}</th>
            <th className={headCell}>{t("notifications.colRecipients")}</th>
            <th className={headCell}>{t("notifications.colChannel")}</th>
            <th className={`${headCell} text-right`}>{t("notifications.colDelivered")}</th>
            <th className={headCell}>{t("notifications.colDate")}</th>
            <th className={headCell}>{t("notifications.colStatus")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-grey-100">
          {isLoading || !data
            ? [0, 1, 2, 3].map((i) => <RowSkeleton key={i} />)
            : data.map((n) => (
                <HistoryRow
                  key={n.id}
                  item={n}
                  lang={lang}
                  onCancel={onCancel}
                  canceling={cancelingId === n.id}
                />
              ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function HistoryCard({
  item,
  lang,
  onCancel,
  canceling,
}: {
  item: SentNotification;
  lang: string;
  onCancel: (id: number) => void;
  canceling: boolean;
}) {
  const { t } = useTranslation();
  const title = lang === "ru" ? item.titleRu : item.titleUz;
  return (
    <li className="px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-[14px] font-semibold leading-snug text-grey-900">{title}</p>
        <span className="flex-none">
          <StatusBadge status={item.status} />
        </span>
      </div>
      <p className="mt-1 text-[12.5px] text-grey-500">
        {item.recipients} · {t(CHANNEL_TABLE_KEY[item.channel])}
      </p>
      <div className="mt-2 flex items-center justify-between gap-3 text-[12.5px]">
        <span className="text-grey-500">{formatNotifDate(item.date, lang)}</span>
        <span className="text-grey-500">
          {t("notifications.colDelivered")}:{" "}
          <b className="tnum font-semibold text-grey-800">{formatCount(item.delivered)}</b>
        </span>
      </div>
      {item.status === "scheduled" && (
        <button
          type="button"
          onClick={() => onCancel(item.id)}
          disabled={canceling}
          className="mt-3 flex h-9 items-center rounded-control border border-grey-200 px-3 text-[12.5px] font-semibold text-grey-600 transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          {canceling ? t("notifications.cancelling") : t("notifications.cancelScheduled")}
        </button>
      )}
    </li>
  );
}

function HistoryCardSkeleton() {
  return (
    <li className="px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <div className="h-3.5 w-40 animate-pulse rounded bg-grey-100" />
        <div className="h-5 w-16 animate-pulse rounded-pill bg-grey-100" />
      </div>
      <div className="mt-2 h-3 w-32 animate-pulse rounded bg-grey-100" />
      <div className="mt-2.5 h-3 w-full animate-pulse rounded bg-grey-100" />
    </li>
  );
}

function HistoryRow({
  item,
  lang,
  onCancel,
  canceling,
}: {
  item: SentNotification;
  lang: string;
  onCancel: (id: number) => void;
  canceling: boolean;
}) {
  const { t } = useTranslation();
  // Show the column matching the dashboard's current language (task 0081).
  const title = lang === "ru" ? item.titleRu : item.titleUz;
  return (
    <tr className="text-[13px]">
      <td className="px-4 py-3 font-semibold text-grey-900">{title}</td>
      <td className="px-4 py-3 text-grey-600">{item.recipients}</td>
      <td className="px-4 py-3 text-grey-600">{t(CHANNEL_TABLE_KEY[item.channel])}</td>
      <td className="tnum px-4 py-3 text-right text-grey-700">{formatCount(item.delivered)}</td>
      <td className="px-4 py-3 text-grey-500">{formatNotifDate(item.date, lang)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <StatusBadge status={item.status} />
          {/* A still-pending scheduled broadcast can be called off before the sweep fires it. */}
          {item.status === "scheduled" && (
            <button
              type="button"
              onClick={() => onCancel(item.id)}
              disabled={canceling}
              className="text-[12px] font-semibold text-grey-500 transition-colors hover:text-red disabled:opacity-50"
            >
              {canceling ? t("notifications.cancelling") : t("notifications.cancelScheduled")}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: SentNotification["status"] }) {
  const { t } = useTranslation();
  const map: Record<
    SentNotification["status"],
    { cls: string; dot: string; label: string }
  > = {
    sent: { cls: "bg-green-bg text-green-dark", dot: "bg-green", label: t("notifications.statusSent") },
    scheduled: {
      cls: "bg-blue-bg-soft text-blue",
      dot: "bg-blue",
      label: t("notifications.statusScheduled"),
    },
    draft: { cls: "bg-grey-100 text-grey-500", dot: "bg-grey-400", label: t("notifications.statusDraft") },
    cancelled: {
      cls: "bg-grey-50 text-grey-400",
      dot: "bg-grey-300",
      label: t("notifications.statusCancelled"),
    },
  };
  const s = map[status] ?? map.sent;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-[12px] font-semibold ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function RowSkeleton() {
  return (
    <tr>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-3.5 w-full max-w-[120px] animate-pulse rounded bg-grey-100" />
        </td>
      ))}
    </tr>
  );
}
